<template>
  <v-container v-if="myRegistration" class="pa-0">
    <div>
      <h1>Film Submission</h1>
    </div>
    <v-stepper v-model="step" non-linear>
      <v-stepper-header>
        <template v-if="!smAndDown" v-for="header in stepperHeaders" :key="`${header.id}-step`">
          <v-stepper-item
            :complete="header.isCompleted"
            :step="header.id"
            :value="header.id"
            :title="header.title"
            :subtitle="!header.isCompleted ? header.subtitle : ''"
            :rules="[() => header.isCompleted == true]"
            :editable="!header.disabled"
            :color="header.isCompleted ? 'green' : ''"></v-stepper-item>

          <v-divider v-if="header.id !== stepperHeaders.length"></v-divider>
        </template>
        <template v-else>
          <v-select
            v-model="step"
            :items="stepperHeaders"
            item-title="title"
            item-value="id"
            label="Select step"
            density="comfortable"
            hide-details
            v-model:menu="menuOpen">
            <template v-slot:item="slotProps">
              <v-list-item
                :key="slotProps.item.raw.id"
                :value="slotProps.item.raw.id"
                @click="
                  step = slotProps.item.raw.id;
                  menuOpen = false;
                ">
                {{ slotProps.item.title }}

                <v-icon v-if="slotProps.item.raw.isCompleted" size="18" color="green"
                  >mdi-check-circle</v-icon
                >

                <v-icon v-else size="18" color="error">mdi-close-circle</v-icon>
              </v-list-item>
            </template>
          </v-select>
        </template>
      </v-stepper-header>
      <v-stepper-window v-model="step">
        <v-stepper-window-item :value="1">
          <TurnInOverviewSubView
            :year="year"
            :registration="myRegistration"></TurnInOverviewSubView>
        </v-stepper-window-item>
        <v-stepper-window-item :value="2">
          <TurnInUploadMovieSubView
            :registration="myRegistration"
            :year="year"
            :team="team"></TurnInUploadMovieSubView>
        </v-stepper-window-item>
        <v-stepper-window-item :value="3">
          <TurnInEntryFormSubView :registration="myRegistration" :year="year" :team="team">
          </TurnInEntryFormSubView>
        </v-stepper-window-item>
        <v-stepper-window-item :value="4">
          <TurnInCriteriaSubView
            :registration="myRegistration"
            :year="year"
            :team="team"></TurnInCriteriaSubView>
        </v-stepper-window-item>
        <v-stepper-window-item :value="5">
          <TurnInTeamVideoSubView
            :registration="myRegistration"
            :year="year"
            :team="team"></TurnInTeamVideoSubView>
        </v-stepper-window-item>
        <v-stepper-window-item :value="6">
          <TurnInCastAndCrewSubView
            :registration="myRegistration"
            :year="year"
            :team="team"></TurnInCastAndCrewSubView>
        </v-stepper-window-item>
        <v-stepper-window-item :value="7">
          <TurnInReleasesSubView
            :registration="myRegistration"
            :year="year"
            :team="team"
            @releaseStatus="updateReleaseStatus"></TurnInReleasesSubView>
        </v-stepper-window-item>
      </v-stepper-window>
      <!-- Action Buttons: Previous and Next -->
      <template v-slot:actions>
        <v-btn color="primary" class="m-3" :disabled="step === 1" @click="step--"> Previous </v-btn>
        <v-btn v-if="step != 7" color="primary" class="float-right m-3" @click="step++">
          Next
        </v-btn>
        <v-btn v-if="step == 7" color="primary" class="float-right m-3" @click="turnInCompleted">
          Done
        </v-btn>
      </template>
    </v-stepper>
  </v-container>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useDisplay } from 'vuetify';
import { useRegistrationStore, useTeamStore, useYearStore } from '@/stores';
import TurnInOverviewSubView from './subviews/TurnInOverviewSubView.vue';
import TurnInUploadMovieSubView from './subviews/TurnInUploadMovieSubView.vue';
import { TeamDTO, YearDTO } from '@/dtos';
import TurnInEntryFormSubView from './subviews/TurnInEntryFormSubView.vue';
import TurnInCriteriaSubView from './subviews/TurnInCriteriaSubView.vue';
import TurnInTeamVideoSubView from './subviews/TurnInTeamVideoSubView.vue';
import TurnInCastAndCrewSubView from './subviews/TurnInCastAndCrewSubView.vue';
import TurnInReleasesSubView from './subviews/TurnInReleasesSubView.vue';
import { useRouter } from 'vue-router';

const registrationStore = useRegistrationStore();
const yearStore = useYearStore();
const teamStore = useTeamStore();
const isTurnInDone = ref<boolean>(false);
const { smAndDown } = useDisplay();
const router = useRouter();
const team = computed(() => teamStore.getMyTeamQuery.data ?? new TeamDTO());
const year = computed(() => yearStore.currentYear ?? new YearDTO());
const myRegistration = computed(() => registrationStore.getMyRegistration.data);
const menuOpen = ref(false);
const step = ref<number>(1); // Track current step

const stepperHeaders = computed(() => [
  { title: 'Overview', isCompleted: true, id: 1, disabled: false },
  {
    title: 'Upload Movie',
    subtitle: 'Missing Film',
    isCompleted: registrationStore.hasMovie,
    id: 2,
    disabled: false,
  },
  {
    title: 'Entry Form',
    subtitle: 'Not Submitted',
    isCompleted: registrationStore.hasSubmittedEntry,
    id: 3,
    disabled: false,
  },
  {
    title: 'Use of Criteria',
    subtitle: 'Missing Data',
    isCompleted: registrationStore.hasCompletedCriteria,
    id: 4,
    disabled: false,
  },
  {
    title: 'Still & Team Video',
    subtitle: 'Missing Still or Team Video',
    isCompleted: registrationStore.hasUploadedTeamAndStill,
    id: 5,
    disabled: false,
  },
  {
    title: 'Cast and Crew',
    subtitle: 'Missing Cast and Crew',
    isCompleted: registrationStore.hasCastCrew,
    id: 6,
    disabled: false,
  },
  {
    title: 'Release Forms',
    subtitle: 'Not Submitted',
    isCompleted: registrationStore.hasReleases,
    id: 7,
    disabled: false,
  },
]);

const updateReleaseStatus = (status: boolean) => {
  isTurnInDone.value = status;
};

const turnInCompleted = async () => {
  router.push({
    name: 'Home',
    params: { tabName: 'info' },
  });
};
</script>
<style></style>
